import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { Upload } from "lucide-react";
import { GetAllExpeditionBoResponseDtoExpedition } from "@/types/api/api-types";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { CountryBadge } from "@/components/shared/badge/country.badge";
import { PackagingTypeBadge } from "@/components/shared/badge/packagingType.badge";
import Paths from "@/constants/paths";
import { FileTypeEnum } from "@/constants/enums/fileType.enum";
import CheckIcon from "@/assets/icons/check.svg?react";
import XIcon from "@/assets/icons/x.svg?react";

const columnHelper = createColumnHelper<GetAllExpeditionBoResponseDtoExpedition>();

type Params = {
  onUploadShippingLabel: (expedition: GetAllExpeditionBoResponseDtoExpedition) => void;
};

export const usePrintShippingLabelsTableColumns = ({
  onUploadShippingLabel,
}: Params): ColumnDef<GetAllExpeditionBoResponseDtoExpedition>[] => {
  const { t } = useTranslation("boExpeditions");
  return useMemo(
    () => [
      {
        id: "select",
        size: 40,
        enableSorting: false,
        enableHiding: false,
        header: ({ table }) =>
          table.options.enableMultiRowSelection === false ? null : (
            <Checkbox
              checked={
                table.getIsAllPageRowsSelected() ||
                (table.getIsSomePageRowsSelected() ? "indeterminate" : false)
              }
              onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
              aria-label={t("printShippingLabelsColumns.selectAll")}
            />
          ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            onClick={(e) => e.stopPropagation()}
            aria-label={t("printShippingLabelsColumns.selectRow")}
            disabled={!row.getCanSelect()}
          />
        ),
      },
      columnHelper.display({
        id: "identifier",
        header: t("printShippingLabelsColumns.identifier"),
        size: "auto" as unknown as number,
        cell: (info) => {
          const color = info.row.original.pickupInfo?.pickupLocationColor || "#a4a4a4a4";
          return (
            <div className="flex h-[45px] items-center gap-2">
              <div
                className={`h-[12px] w-[12px] rounded-full`}
                style={{ backgroundColor: color }}
              />
              <div className="w-fit text-base">
                {info.row.original.quotation
                  ? "C-" + info.row.original.quotation?.identifier
                  : "D-" + info.row.original.customQuotation?.identifier}
              </div>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: "ltr",
        header: t("printShippingLabelsColumns.shippingLabel"),
        meta: `justify-center`,
        size: "auto" as unknown as number,
        cell: (info) => {
          const hasShippingLabel = info.row.original.expeditionDocuments?.documents?.some(
            (document) => document.type === FileTypeEnum.EXPEDITION_SHIPPING_LABEL,
          );

          return hasShippingLabel ? (
            <CheckIcon width={20} height={20} color="#06F" />
          ) : (
            <div className="flex items-center gap-2">
              <XIcon width={20} height={20} color="#ff0000" />
              <Button
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onUploadShippingLabel(info.row.original);
                }}
              >
                <Upload className="h-4 w-4" />
              </Button>
            </div>
          );
        },
      }),
      {
        id: "finalizedAt",
        header: t("printShippingLabelsColumns.finalizedAt"),
        sortDescFirst: false,
        accessorKey: "finalizedAt",
        size: "auto" as unknown as number,
        cell: (info) => {
          if (!info.row.original.finalizedAt) {
            return <></>;
          }
          return (
            <div className="flex w-full flex-row items-center justify-center gap-2">
              <p className="max-w-[200px] overflow-hidden text-left text-sm font-medium text-ellipsis text-gray-600">
                {info.row.original.finalizedBy?.firstName?.charAt(0).toUpperCase() ?? ""}
                {info.row.original.finalizedBy?.lastName?.charAt(0).toUpperCase() ?? ""}
              </p>
              <div className="flex w-fit flex-col items-center gap-0">
                <p className="text-base font-bold text-gray-900">
                  {new Date(info.row.original.finalizedAt).toLocaleDateString("fr-FR")}
                </p>
                <p className="text-sm font-bold text-gray-600">
                  {t("printShippingLabelsColumns.atTime", {
                    time: new Date(info.row.original.finalizedAt).toLocaleTimeString("fr-FR", {
                      hour: "2-digit",
                      minute: "2-digit",
                    }),
                  })}
                </p>
              </div>
            </div>
          );
        },
      },
      columnHelper.display({
        id: "client",
        header: t("printShippingLabelsColumns.client"),
        size: "auto" as unknown as number,
        cell: (info) => (
          <div className="max-w-[300px] truncate text-base font-bold">
            {info.row.original.company.businessName}
          </div>
        ),
      }),
      columnHelper.display({
        id: "clientDest",
        header: t("printShippingLabelsColumns.clientDest"),
        size: "auto" as unknown as number,
        cell: (info) => {
          const client =
            info.row.original.deliveryInfo.company ||
            (info.row.original.deliveryInfo.firstName && info.row.original.deliveryInfo.lastName
              ? info.row.original.deliveryInfo.firstName +
                " " +
                info.row.original.deliveryInfo.lastName
              : "-");
          return <div className="max-w-[300px] truncate text-base font-bold">{client}</div>;
        },
      }),
      columnHelper.display({
        id: "country",
        header: t("printShippingLabelsColumns.country"),
        size: "auto" as unknown as number,
        cell: (info) => <CountryBadge country={info.row.original.deliveryInfo?.country || "-"} />,
      }),
      {
        id: "tracking",
        header: t("printShippingLabelsColumns.tracking"),
        size: "auto" as unknown as number,
        cell: (info) => {
          const tracking = info.row.original.tracking;

          if (tracking?.manualTracking === false && tracking?.trackingCode) {
            return (
              <Button
                variant="link"
                className="max-w-[150px] truncate overflow-hidden text-sm font-medium"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(`${Paths.others.tracking}?code=${info.row.original.id}`, "_blank");
                }}
              >
                {tracking.trackingCode}
              </Button>
            );
          } else if (tracking?.manualTracking === true && tracking?.trackingUrl) {
            return (
              <Button
                variant="link"
                className="max-w-[150px] truncate overflow-hidden text-sm font-medium"
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(tracking.trackingUrl, "_blank");
                }}
              >
                {t("printShippingLabelsColumns.trackingLink")}
              </Button>
            );
          }
          return <div className="w-fit text-base">-</div>;
        },
      },
      {
        id: "bottlesCount",
        header: t("printShippingLabelsColumns.bottlesCount"),
        size: "auto" as unknown as number,
        cell: (info) => {
          const quotation = info.row.original.quotation;

          if (!quotation) {
            return <div className="w-fit text-base">-</div>;
          }

          const nbBottles =
            quotation.deliveryContent?.reduce((acc, content) => acc + content.numberOfBottles, 0) ||
            quotation.quotationProducts?.reduce(
              (acc, product) => acc + product.bottleQuantity,
              0,
            ) ||
            0;

          return <div className="w-fit text-base">{nbBottles}</div>;
        },
      },
      {
        id: "volume",
        header: t("printShippingLabelsColumns.volume"),
        size: "auto" as unknown as number,
        cell: (info) => {
          const quotation = info.row.original.quotation;

          if (!quotation) {
            return <div className="w-fit text-base">-</div>;
          }

          const totalVolume =
            quotation.deliveryContent?.reduce(
              (acc, content) => acc + content.numberOfBottles * content.volume,
              0,
            ) ||
            quotation.quotationProducts?.reduce(
              (acc, product) => acc + product.bottleQuantity * product.volume,
              0,
            ) ||
            0;

          return (
            <div className="w-fit text-base">
              {t("printShippingLabelsColumns.volumeValue", { volume: totalVolume })}
            </div>
          );
        },
      },
      {
        id: "packaging",
        header: t("printShippingLabelsColumns.packaging"),
        meta: `justify-center`,
        size: "auto" as unknown as number,
        cell: (info) => <PackagingTypeBadge expedition={info.row.original} />,
      },
    ],
    [t, onUploadShippingLabel],
  );
};
